import { Typography } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';

import { Button, DocumentInput, Form, Space } from '~/components';
import features from '~/constants/features';
import { useGenerateContract, useUploadSignedContract } from '~/controllers/contract';
import { useGetApplication } from '~/controllers/wizard';
import { useAuthorizedSignatories } from '~/hooks';
import { ContractGenerationIcon } from '~/img';
import BottomLeftActions from '~/layouts/BottomLeftActions';
import LeftLayout from '~/layouts/LeftLayout';
import Section from '~/layouts/Section ';
import WizardContent from '~/layouts/WizardContent';
import WizardContinueButton from '~/layouts/WizardContinueButton';
import paths from '~/pages/paths';
import { ImageResponse } from '~/types/files';
import { isSales } from '~/utils/getDecodedJwt';
import { isReadOnly } from '~/utils/isReadOnly';

import ApplicationCommonErrors from '../ApplicationCommonErrors';
import SaveContract from './SaveContract';
import SendInvitationModal from './SendInvitationModal';
import SignatorySection from './SignatorySection';

const NonUaePassSignContract: React.FC = () => {
  const { t } = useTranslation('contract');
  const applicationQuery = useGetApplication();
  const generateContractMutation = useGenerateContract();
  const uploadContractMutation = useUploadSignedContract();
  const location = useLocation();
  const history = useHistory();
  const sales = isSales();

  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isContractUploaded, setIsContractUploaded] = useState<boolean>(false);

  const readOnly = isReadOnly(applicationQuery.data);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    void applicationQuery.refetch();
  };

  const {
    isMultipleSignatories,
    selectedAuthorizedRepresentativeIndex,
    authorizedSignatoryEmail,
    setAuthorizedSignatoryEmail,
    resultingAuthorizedRepresentative,
    isCurrentUserAuthorizedSignatory,
  } = useAuthorizedSignatories();

  const handleGenerateClick = () => {
    if (isMultipleSignatories === null) return;
    if (isMultipleSignatories && !resultingAuthorizedRepresentative && !isCurrentUserAuthorizedSignatory) {
      handleOpen();
    } else {
      generateContract();
    }
  };

  const generateContract = () => {
    generateContractMutation.mutate();
  };

  const handleClick = () => {
    setIsLoading(true);
  };

  const onFinish = async (values: { data: ImageResponse | ImageResponse[] }) => {
    try {
      if (Array.isArray(values.data)) {
        await uploadContractMutation.mutateAsync(values.data);
      } else {
        await uploadContractMutation.mutateAsync([values.data]);
      }
      history.push(paths.home._);
    } catch (error) {
      console.error('An error occurred while proceeding to the next step', error);
      setIsLoading(false);
    }
  };

  const signatorySection = !isCurrentUserAuthorizedSignatory && isMultipleSignatories && (
    <>
      <SignatorySection
        resultingAuthorizedRepresentative={
          resultingAuthorizedRepresentative && typeof resultingAuthorizedRepresentative !== 'string'
            ? resultingAuthorizedRepresentative
            : undefined
        }
        onOpen={handleOpen}
        readOnly={readOnly}
        authorizedSignatoryEmail={authorizedSignatoryEmail}
        isCurrentUserAuthorizedSignatory={isCurrentUserAuthorizedSignatory}
      />
    </>
  );
  return (
    <>
      <SendInvitationModal
        open={open}
        onClose={handleClose}
        onInvitationSent={generateContract}
        authorizedSignatoryEmail={authorizedSignatoryEmail}
        setAuthorizedSignatoryEmail={setAuthorizedSignatoryEmail}
        isCurrentUserAuthorizedSignatory={isCurrentUserAuthorizedSignatory}
      />
      <WizardContent
        content={
          <Container>
            <Typography.Title>{t('Commercial Card Application')}</Typography.Title>
            {!(generateContractMutation.data || sales) ? (
              <Section formItemLast>
                {signatorySection}
                <Typography.Paragraph>
                  {t('To generate a copy of the complete application form please press the button below.')}
                </Typography.Paragraph>
                <Space direction="vertical" size={24}>
                  <Button
                    id="signContract_generateContract"
                    extraLarge
                    icon={<ContractGenerationIcon />}
                    type="default"
                    onClick={handleGenerateClick}
                    loading={generateContractMutation.isLoading || applicationQuery.isLoading}
                    disabled={applicationQuery.isLoading || readOnly}
                  >
                    {t('Generate Completed Application')}
                  </Button>
                </Space>
              </Section>
            ) : (
              <>
                {!sales && (
                  <Section formItemLast>
                    {signatorySection}
                    <Typography.Title level={5}>{t('Download Contract')}</Typography.Title>
                    <SaveContract
                      name={generateContractMutation?.data?.name || ''}
                      data={generateContractMutation?.data?.blob || undefined}
                    />
                  </Section>
                )}
                <Form name={location.pathname} onFinish={onFinish}>
                  <Section>
                    {(isCurrentUserAuthorizedSignatory || sales) && (
                      <>
                        <Typography.Title level={5}>{t('Please Sign and Upload the Contract')}</Typography.Title>
                        <Space direction="vertical" size={24}>
                          <Form.Item name="data">
                            <DocumentInput
                              name="contract"
                              variant="default"
                              itemRender={(originNode, _, fileList) => {
                                const hasValidFiles = fileList?.some(
                                  (file) => file.status === 'success' && file.thumbUrl,
                                );
                                if (hasValidFiles) {
                                  setIsContractUploaded(true);
                                } else {
                                  setIsContractUploaded(false);
                                }
                                return originNode;
                              }}
                            />
                          </Form.Item>
                        </Space>
                      </>
                    )}
                  </Section>
                </Form>
              </>
            )}
          </Container>
        }
        left={
          <LeftLayout>
            <BottomLeftActions contactUsUrl={features.contactUsLinkContract} />
          </LeftLayout>
        }
        right={
          <Space direction="vertical" size={24}>
            <ApplicationCommonErrors />
            <WizardContinueButton
              id="contract_continueButton"
              formName={location.pathname}
              disabled={
                (!sales || !isContractUploaded) &&
                (!isContractUploaded ||
                  (selectedAuthorizedRepresentativeIndex >= 0 && !isCurrentUserAuthorizedSignatory))
              }
              loading={isLoading}
              onClick={handleClick}
            />
          </Space>
        }
      />
    </>
  );
};

const Container = styled.div`
  h1 {
    margin-bottom: 16px;
  }
  .SignContract-Subtitle {
    margin-bottom: 24px;
  }
`;

export default NonUaePassSignContract;
